// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import L from 'leaflet';
import proj4 from 'proj4';
import 'proj4leaflet';

export function formatCRSCoords(name: string, point: L.Point): string {
  const roundedEasting = Math.round(point.x / 1000) * 1000;
  const roundedNorthing = Math.round(point.y / 1000) * 1000;
  return `${name}:${roundedEasting}, ${roundedNorthing}`;
}

export enum CRSCode {
  VG = 'EPSG:7899',
  GDA2020 = 'EPSG:7844',
  MGA55 = 'EPSG:7855',
  MGA54 = 'EPSG:7854',
  GDA94 = 'EPSG:4283',
  MGA55_94 = 'EPSG:28355',
  MGA54_94 = 'EPSG:28354',
}

// Vicgrid
const EPSG7899 =
  '+proj=lcc +lat_0=-37 +lon_0=145 +lat_1=-36 +lat_2=-38 +x_0=2500000 +y_0=2500000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs';

// GDA2020
const EPSG7844 =
  '+proj=longlat +ellps=GRS80 +towgs84=-0.06155,0.01087,0.04019,-0.0394924,-0.0327221,-0.0328979,0.009994 +no_defs';

// GDA2020 / MGA zone 55
const EPSG7855 =
  '+proj=utm +zone=55 +south +ellps=GRS80 +towgs84=-0.06155,0.01087,0.04019,-0.0394924,-0.0327221,-0.0328979,0.009994 +units=m +no_defs +type=crs';

// GDA2020 / MGA zone 54
const EPSG7854 =
  '+proj=utm +zone=54 +south +ellps=GRS80 +towgs84=-0.06155,0.01087,0.04019,-0.0394924,-0.0327221,-0.0328979,0.009994 +units=m +no_defs +type=crs';

// GDA94
const EPSG4939 = '+proj=longlat +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +no_defs';

// GDA94 / MGA zone 55
const EPSG28355 =
  '+proj=utm +zone=55 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs';

// GDA94 / MGA zone 54
const EPSG28354 =
  '+proj=utm +zone=54 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs';

// Register projections with proj4
proj4.defs(CRSCode.GDA2020, EPSG7844);
proj4.defs(CRSCode.MGA54, EPSG7854);
proj4.defs(CRSCode.MGA55, EPSG7855);
proj4.defs(CRSCode.GDA94, EPSG4939);
proj4.defs(CRSCode.MGA54_94, EPSG28354);
proj4.defs(CRSCode.MGA55_94, EPSG28355);

const tms = {
  TileMatrixSet: {
    Identifier: 'EPSG:7899',
    SupportedCRS: 'EPSG:7899',
    TileMatrix: [
      {
        Identifier: '00',
        ScaleDenominator: '7559538.928601667',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '2',
        MatrixHeight: '1',
      },
      {
        Identifier: '01',
        ScaleDenominator: '3779769.4643008336',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '4',
        MatrixHeight: '2',
      },
      {
        Identifier: '02',
        ScaleDenominator: '1889884.7321504168',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '8',
        MatrixHeight: '4',
      },
      {
        Identifier: '03',
        ScaleDenominator: '944942.3660752084',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '16',
        MatrixHeight: '8',
      },
      {
        Identifier: '04',
        ScaleDenominator: '472471.1830376042',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '32',
        MatrixHeight: '16',
      },
      {
        Identifier: '05',
        ScaleDenominator: '236235.5915188021',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '64',
        MatrixHeight: '32',
      },
      {
        Identifier: '06',
        ScaleDenominator: '94494.23660752083',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '160',
        MatrixHeight: '80',
      },
      {
        Identifier: '07',
        ScaleDenominator: '47247.11830376041',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '320',
        MatrixHeight: '160',
      },
      {
        Identifier: '08',
        ScaleDenominator: '23623.559151880207',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '640',
        MatrixHeight: '320',
      },
      {
        Identifier: '09',
        ScaleDenominator: '9449.423660752083',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '1600',
        MatrixHeight: '800',
      },
      {
        Identifier: '10',
        ScaleDenominator: '4724.711830376042',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '3200',
        MatrixHeight: '1600',
      },
      {
        Identifier: '11',
        ScaleDenominator: '2362.355915188021',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '6400',
        MatrixHeight: '3200',
      },
      {
        Identifier: '12',
        ScaleDenominator: '1181.1779575940104',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '12800',
        MatrixHeight: '6400',
      },
      {
        Identifier: '13',
        ScaleDenominator: '755.9538928601667',
        TopLeftCorner: '1786000.0 3081000.0',
        TileWidth: '512',
        TileHeight: '512',
        MatrixWidth: '20000',
        MatrixHeight: '10000',
      },
    ],
  },
};

// extract layer scales from getcapabilities JSON dump
const scales = tms.TileMatrixSet.TileMatrix.map((i) =>
  parseFloat(i.ScaleDenominator),
);

// get origin from tile matrix
const origin = tms.TileMatrixSet.TileMatrix[0].TopLeftCorner.split(' ').map(
  (val) => parseFloat(val),
) as [number, number];

// get tile size from tile matrix
export const wmtsTileSize = parseFloat(
  tms.TileMatrixSet.TileMatrix[0].TileWidth,
);

// Multiply resolutions by standard pixel size
const standardPixelSize = 0.00028;
const resolutions = [
  ...scales.map((scale) => scale * standardPixelSize),
  0.1,
  0.05,
];

export const VicGridCRS = new L.Proj.CRS(CRSCode.VG, EPSG7899, {
  origin: origin,
  resolutions: resolutions,
});

export const MGA54CRS = new L.Proj.CRS(CRSCode.MGA54, EPSG7854, {});

export const MGA55CRS = new L.Proj.CRS(CRSCode.MGA55, EPSG7855, {});

export const MGA_54_55_THRESHOLD = 144;

export function convertToOtherGDA(
  latitude: number,
  longitude: number,
  datumCode: string,
) {
  if (!['GDA94', 'GDA2020'].includes(datumCode)) {
    throw new Error('Invalid datum code. Must be GDA94 or GDA2020');
  }

  let newDatumCode = '';
  if (datumCode === 'GDA2020') {
    newDatumCode = 'GDA94';
  } else if (datumCode === 'GDA94') {
    newDatumCode = 'GDA2020';
  }

  const fromProj = datumCode === 'GDA2020' ? CRSCode.GDA2020 : CRSCode.GDA94;
  const toLatLngProj =
    newDatumCode === 'GDA2020' ? CRSCode.GDA2020 : CRSCode.GDA94;

  const newLatLng = proj4(fromProj, toLatLngProj, [longitude, latitude]);

  return {
    latitude: Number(newLatLng[1].toFixed(6)),
    longitude: Number(newLatLng[0].toFixed(6)),
    datumCode: newDatumCode,
  };
}

export function convertMGAToLatLng(
  easting: number,
  northing: number,
  zone: number,
  datumCode: string,
) {
  if (!['GDA94', 'GDA2020'].includes(datumCode)) {
    throw new Error('Invalid datum code. Must be GDA94 or GDA2020');
  }
  if (![54, 55].includes(zone)) {
    throw new Error('Invalid zone. Must be 54 or 55');
  }
  const base = datumCode === 'GDA2020' ? CRSCode.MGA54 : CRSCode.MGA54_94;

  const fromProj = datumCode === 'GDA2020' ? CRSCode.GDA2020 : CRSCode.GDA94;

  const latlng = proj4(base.slice(0, -2) + zone.toString(), fromProj, [
    easting,
    northing,
  ]);

  return {
    latitude: Number(latlng[1].toFixed(6)),
    longitude: Number(latlng[0].toFixed(6)),
  };
}

export function convertLatLngToMga(
  latitude: number,
  longitude: number,
  datumCode: string,
  precision = 0,
) {
  if (!['GDA94', 'GDA2020'].includes(datumCode)) {
    throw new Error('Invalid datum code. Must be GDA94 or GDA2020');
  }

  const base = datumCode === 'GDA2020' ? CRSCode.MGA54 : CRSCode.MGA54_94;
  const fromProj = datumCode === 'GDA2020' ? CRSCode.GDA2020 : CRSCode.GDA94;

  const zone = longitude > MGA_54_55_THRESHOLD ? 55 : 54;

  const eastNorth = proj4(fromProj, base.slice(0, -2) + zone.toString(), [
    longitude,
    latitude,
  ]);

  const normalizedPrecision = Math.max(0, Math.trunc(precision));

  return {
    easting: Number(eastNorth[0].toFixed(normalizedPrecision)),
    northing: Number(eastNorth[1].toFixed(normalizedPrecision)),
    zone: zone,
  };
}
